import { useTranslation } from "react-i18next";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import Circle from "../../oasis/yards-section/components/Circle";
import { TIMEZONE_OPTIONS } from "../../../Layouts/admin/components/sections/trips/types";
import { WATER_TYPES as YARD_TYPES } from "../components/waterTypes";
import YardOperationPanel from "./YardOperationPanel";
import type { ThemeClasses, Yard } from "./types";

// La modal de detalles vive en su propio archivo desde #499: `page.tsx` tenía
// 2.110 líneas con la fila de lista, la tarjeta de grilla, esta modal, la modal
// de formulario y la página entera, y las issues #499 y #501 tocan las tarjetas
// en serie sobre ese mismo archivo. Sacar los 351 renglones que ninguna de las
// dos necesita leer deja el diff del cambio de comportamiento revisable.
//
// Es una MUDANZA: el único cambio es que la etiqueta de la zona horaria se lee
// directo del mapa `TIMEZONE_OPTIONS` en vez de recorrer el arreglo `TIMEZONES`
// que `page.tsx` derivaba de ese mismo mapa. Misma etiqueta y la misma
// alternativa (la zona cruda) cuando no está en el catálogo.
//
// Radio, teléfono de caseta y zona horaria siguen acá: #499 los saca de las
// tarjetas —son configuración, no operación— pero no los borra.

function YardDetailsModal({
  yard,
  themeClasses,
  isDarkMode,
  isDeleting,
  userLabelById,
  deviceLabelById,
  onClose,
  onEdit,
  onDelete,
  canDelete = true,
}: {
  yard: Yard;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isDeleting: boolean;
  userLabelById: Record<string, string>;
  deviceLabelById: Record<string, string>;
  onClose: () => void;
  onEdit: (y: Yard) => void;
  onDelete: (y: Yard) => void;
  canDelete?: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const labelColor = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto overflow-x-hidden`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            {t("yards.details.title")}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-5">
          {/* La operación va ARRIBA de la configuración (#501). No es un detalle
              de maquetado: la modal mostraba ubicación, radio, teléfono de
              caseta, zona horaria, geocerca, usuarios y dispositivos —cero
              operación—, y el hito entero se trata de que el patio se vea por lo
              que está pasando adentro y no por cómo quedó configurado hace seis
              meses. El orden de la modal es esa misma afirmación. */}
          <YardOperationPanel
            yard={yard}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
          />

          <div className={`border-t ${themeClasses.cardBorder}`} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column - Info */}
            <div className="space-y-5">
              {/* Name + ID + Status */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {yard.logo && yard.logo !== "null" && yard.logo !== "" ? (
                    <img
                      src={yard.logo}
                      alt={yard.name}
                      className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                      onError={(e) => {
                        // Si la imagen falla al cargar, mostrar el icono por defecto
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextElementSibling?.classList.remove(
                          "hidden",
                        );
                      }}
                    />
                  ) : null}
                  {(!yard.logo || yard.logo === "null" || yard.logo === "") && (
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/20">
                      <i className="ri-building-4-line text-amber-500 text-lg"></i>
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className={`text-base font-bold ${themeClasses.text}`}>
                      {yard.name}
                    </h3>
                    <span
                      className={`text-xs font-mono ${themeClasses.textSecondary}`}
                    >
                      {yard.id}
                    </span>
                  </div>
                </div>

                {/* Status badge */}
                <span
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium flex-shrink-0 ${
                    yard.status === "active"
                      ? "bg-green-500/15 text-green-400 border border-green-500/30"
                      : "bg-red-500/15 text-red-400 border border-red-500/30"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      yard.status === "active" ? "bg-green-400" : "bg-red-400"
                    }`}
                  />
                  {yard.status === "active" ? t("yards.card.active") : t("yards.card.inactive")}
                </span>
              </div>

              {/* Info cards */}
              <div className="space-y-3">
                <div className={`${surface} rounded-xl p-4 space-y-3`}>
                  <div className="flex items-center gap-3">
                    <i className={`ri-map-pin-line ${labelColor} text-sm`}></i>
                    <div>
                      <p
                        className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium`}
                      >
                        {t("yards.details.location")}
                      </p>
                      <p className={`text-sm ${themeClasses.text}`}>
                        {yard.location}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`${surface} rounded-xl p-4 grid grid-cols-2 gap-4`}
                >
                  <div>
                    <p
                      className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                    >
                      {t("yards.details.radius")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {yard.radius} m
                    </p>
                  </div>
                  <div>
                    <p
                      className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                    >
                      {t("yards.details.phone")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {yard.guardHousePhone}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p
                      className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                    >
                      {t("yards.details.timezone")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {(yard.timeZone
                        ? TIMEZONE_OPTIONS[
                            yard.timeZone as keyof typeof TIMEZONE_OPTIONS
                          ]
                        : undefined) ?? yard.timeZone}
                    </p>
                  </div>
                </div>

                {yard.type && yard.type.length > 0 && (
                  <div className={`${surface} rounded-xl p-4`}>
                    <p
                      className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                    >
                      {t("yards.details.yardType")}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {yard.type.map((typeValue: string) => {
                        const type = YARD_TYPES.find(
                          (yt) => yt.value === typeValue,
                        );
                        return (
                          <span
                            key={typeValue}
                            className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${
                              isDarkMode
                                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                : "bg-blue-50 text-blue-700 border border-blue-200"
                            }`}
                          >
                            {type?.label ?? typeValue}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {yard.privacyPolicies && (
                  <div className={`${surface} rounded-xl p-4`}>
                    <p
                      className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}
                    >
                      {t("yards.details.privacyPolicies")}
                    </p>
                    <p
                      className={`text-sm ${themeClasses.textSecondary} leading-relaxed`}
                    >
                      {yard.privacyPolicies}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right column - Map */}
            <div className="flex flex-col min-h-[400px]">
              <div
                className={`rounded-xl overflow-hidden border ${themeClasses.cardBorder} relative flex-1 min-h-[400px]`}
              >
                <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                  <Map
                    defaultCenter={
                      yard.latitude && yard.longitude
                        ? { lat: yard.latitude, lng: yard.longitude }
                        : { lat: 25.6866, lng: -100.3161 }
                    }
                    defaultZoom={16}
                    mapId="truck-yard-details-map"
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    zoomControl={true}
                    mapTypeId="roadmap"
                    style={{ width: "100%", height: "100%" }}
                  >
                    {yard.latitude && yard.longitude && (
                      <>
                        <Marker
                          position={{ lat: yard.latitude, lng: yard.longitude }}
                        />
                        <Circle
                          center={{ lat: yard.latitude, lng: yard.longitude }}
                          radius={yard.radius}
                          strokeColor={isDarkMode ? "#10b981" : "#059669"}
                          strokeOpacity={0.8}
                          strokeWeight={2}
                          fillColor={isDarkMode ? "#10b981" : "#059669"}
                          fillOpacity={0.1}
                          editable={false}
                          draggable={false}
                        />
                      </>
                    )}
                  </Map>
                </APIProvider>
              </div>

            </div>
          </div>

          {/* Usuarios notificados (izq.) y dispositivos asignados (der.) */}
          {((yard.notifiedUsers && yard.notifiedUsers.length > 0) ||
            (yard.devices && yard.devices.length > 0)) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className={`${surface} rounded-xl p-4`}>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-2`}
                >
                  {t("yards.details.notifiedUsers")} ({yard.notifiedUsers?.length || 0})
                </p>
                {yard.notifiedUsers && yard.notifiedUsers.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto pr-1">
                    {yard.notifiedUsers.map((uid) => (
                      <span
                        key={uid}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          isDarkMode
                            ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                            : "bg-purple-50 text-purple-700 border border-purple-200"
                        }`}
                        title={userLabelById[uid] || uid}
                      >
                        <i className="ri-user-line text-[10px]"></i>
                        {userLabelById[uid] || uid}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={`text-xs italic ${themeClasses.textSecondary}`}>
                    {t("yards.details.noNotifiedUsers")}
                  </span>
                )}
              </div>

              <div className={`${surface} rounded-xl p-4`}>
                <p
                  className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-2`}
                >
                  {t("yards.details.assignedDevices")} ({yard.devices?.length || 0})
                </p>
                {yard.devices && yard.devices.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto pr-1">
                    {yard.devices.map((did) => (
                      <span
                        key={did}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          isDarkMode
                            ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                            : "bg-cyan-50 text-cyan-700 border border-cyan-200"
                        }`}
                        title={deviceLabelById[did] || did}
                      >
                        <i className="ri-device-line text-[10px]"></i>
                        {deviceLabelById[did] || did}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={`text-xs italic ${themeClasses.textSecondary}`}>
                    {t("yards.details.noAssignedDevices")}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div
          className={`flex justify-end gap-3 pt-4 border-t ${themeClasses.cardBorder} px-4 sm:px-6 pb-6`}
        >
          <button
            onClick={() => onEdit(yard)}
            className={`px-5 py-2.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.buttonText} font-medium text-sm transition-colors`}
          >
            {t("common.edit")}
          </button>
          {canDelete && (
            <button
              onClick={() => onDelete(yard)}
              disabled={isDeleting}
              className="px-5 py-2.5 bg-red-500 text-white rounded-xl font-medium text-sm transition-colors flex items-center gap-2 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {t("yards.details.deleting")}
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-line"></i>
                  {t("common.delete")}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default YardDetailsModal;
